import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, CircleHelp, Eye, EyeOff, LockKeyhole, LogOut, Menu, X, UserRound } from 'lucide-react';
import PersonalInfoModal from '../components/PersonalInfoModal';
import { updateSession } from '../services/session';
import { changePassword, logout, getSessionUser } from '../services/auth';
import { useAuth } from '../auth/AuthProvider';
import Toast from '../components/Toast';
import PageContentLoading from '../components/PageContentLoading';
import '../pages/warehouses/warehouses.css';
import Sidebar from './Sidebar';
import '../styles/app-layout.css';

const passwordHelp = 'Use at least 8 characters with a mix of letters, numbers and symbols.';
const passwordMessages = {
  'New password must be at least 8 characters.': passwordHelp,
  'New password must be different from your current password.': 'New password must be different from the current password.',
};

function validatePasswordField(field, values) {
  if (!values[field].trim()) return {
    currentPassword: 'Current password is required.',
    newPassword: 'New password is required.',
    confirmPassword: 'Confirm new password is required.',
  }[field];
  if (field === 'newPassword' && values.newPassword.length < 8) return passwordHelp;
  if (field === 'confirmPassword' && values.newPassword !== values.confirmPassword) {
    return 'New password and confirm password do not match.';
  }
  return '';
}

export default function MainLayout() {
  const { user } = useAuth();
  const nameParts = user?.name?.trim().split(/\s+/).filter(Boolean) || [];
  const initials = (nameParts.length > 1
    ? nameParts[0][0] + nameParts[nameParts.length - 1][0]
    : nameParts[0]?.[0] || user?.email?.[0] || '').toUpperCase();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pageReset, setPageReset] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [personalInfoOpen, setPersonalInfoOpen] = useState(false);
  const [personalInfoUser, setPersonalInfoUser] = useState(null);
  const profileLoading = React.useRef(false);
  async function openPersonalInfo() {
    if (profileLoading.current) return;
    profileLoading.current = true;
    setProfileOpen(false);
    try {
      const currentUser = await getSessionUser();
      updateSession(currentUser);
      setPersonalInfoUser(currentUser);
      setPersonalInfoOpen(true);
    } catch (error) { notify(error.message, 'error'); }
    finally { profileLoading.current = false; }
  }
  const closePersonalInfo = React.useCallback(() => setPersonalInfoOpen(false), []);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [visible, setVisible] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const profileRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const dialogRef = React.useRef(null);
  const pending = React.useRef(false);
  const touchedPasswords = React.useRef({});
  const toastTimer = React.useRef(null);

  function notify(message, tone = 'success') {
    clearTimeout(toastTimer.current);
    setToast({ message, tone });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }
  React.useEffect(() => () => clearTimeout(toastTimer.current), []);
  React.useEffect(() => {
    if (!profileOpen) return undefined;
    profileRef.current?.querySelector('[role="menuitem"]')?.focus();
    function outside(event) {
      if (!profileRef.current?.contains(event.target)) setProfileOpen(false);
    }
    function key(event) {
      if (event.key === 'Escape') { setProfileOpen(false); triggerRef.current?.focus(); }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const items = [...profileRef.current.querySelectorAll('[role="menuitem"]')];
        const index = items.indexOf(document.activeElement);
        items[(index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
      }
    }
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', key); };
  }, [profileOpen]);
  React.useEffect(() => {
    if (!passwordOpen) {
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setVisible({}); setErrors({}); touchedPasswords.current = {};
      return undefined;
    }
    const dialog = dialogRef.current;
    const trigger = triggerRef.current;
    dialog.querySelector('input')?.focus();
    function key(event) {
      if (event.key === 'Escape' && !pending.current) { setPasswordOpen(false); }
      if (event.key === 'Tab') {
        const items = [...dialog.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
        const first = items[0]; const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('keydown', key); trigger?.focus(); };
  }, [passwordOpen]);
  function openPassword() {
    setProfileOpen(false); setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setErrors({}); setVisible({}); touchedPasswords.current = {}; setPasswordOpen(true);
  }
  function closePassword() { if (!pending.current) setPasswordOpen(false); }
  async function submitPassword(event) {
    event.preventDefault();
    if (pending.current) return;
    const next = {};
    for (const field of Object.keys(passwords)) {
      touchedPasswords.current[field] = true;
      const error = validatePasswordField(field, passwords);
      if (error) next[field] = error;
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    pending.current = true; setBusy(true);
    try {
      await changePassword(passwords);
      setPasswordOpen(false); setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      notify('Password updated successfully.');
    } catch (error) {
      const field = error.message.startsWith('Current password') || error.message.startsWith('Password changed during')
        ? 'currentPassword' : error.message.startsWith('New password') ? 'newPassword' : 'api';
      setErrors({ [field]: passwordMessages[error.message] || error.message });
      dialogRef.current?.querySelector(field === 'api' ? 'input' : `#profile-${field}`)?.focus();
    } finally { pending.current = false; setBusy(false); }
  }
  async function signOut() {
    setProfileOpen(false);
    if (pending.current) return;
    pending.current = true; setBusy(true);
    try { await logout(); navigate('/login', { replace: true }); }
    catch (error) {
      if (error.status === 401) navigate('/login', { replace: true });
      else { notify(error.message, 'error'); triggerRef.current?.focus(); }
    } finally { pending.current = false; setBusy(false); }
  }
  React.useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  React.useEffect(() => { document.title = 'Import ERP'; }, []);

  return <div className={`app-shell${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
    {menuOpen && <button className="app-backdrop" type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}
    <Sidebar open={menuOpen} collapsed={sidebarCollapsed} onToggleCollapsed={() => setSidebarCollapsed(value => !value)} onNavigate={event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      setMenuOpen(false);
      const link = event.currentTarget;
      navigate(`${link.pathname}${link.search}${link.hash}`, {
        state: {
          sidebarReset: Math.max(pageReset, location.state?.sidebarReset || 0) + 1,
          ...(link.classList.contains('app-new-container') ? { createContainerDraft: true } : {}),
        },
      });
      if (event.currentTarget.pathname === location.pathname) setPageReset(value => value + 1);
    }} />
    <div className="app-main">
      <header className="app-header">
        <button className="app-menu-toggle" type="button" aria-label="Open menu" onClick={() => setMenuOpen(true)}><Menu size={16} /></button>
        <div className="app-header-tools">
          <button className="app-icon-btn" type="button" aria-label="Notifications"><Bell size={15} /></button>
          <button className="app-icon-btn" type="button" aria-label="Help"><CircleHelp size={15} /></button>
          <div className="app-profile" ref={profileRef} onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) setProfileOpen(false);
          }}>
          <button className="app-user" type="button" ref={triggerRef} aria-label="User profile" aria-haspopup="menu" aria-expanded={profileOpen} aria-controls={profileOpen ? 'profile-menu' : undefined} disabled={busy} onClick={() => setProfileOpen(value => !value)}>
            <span className="app-avatar" aria-hidden="true">{initials}</span>
            <span className="app-user-meta">
              <strong>{user?.name || user?.email}</strong>
              <span>Workspace user</span>
            </span>
            <ChevronDown size={14} />
          </button>
          {profileOpen && <div className="app-profile-menu" id="profile-menu" role="menu" aria-label="User profile">
            <button type="button" role="menuitem" onClick={openPersonalInfo}><UserRound size={17} />Change Personal Info</button>
            <button type="button" role="menuitem" onClick={openPassword}><LockKeyhole size={17} />Change Password</button>
            <button type="button" role="menuitem" onClick={signOut}><LogOut size={17} />Logout</button>
          </div>}
          </div>
        </div>
      </header>
      <div className="app-content"><PageContentLoading><Outlet key={`${location.state?.sidebarReset || 0}-${Boolean(location.state?.createContainerDraft)}`} /></PageContentLoading></div>
    </div>
    {personalInfoOpen && <PersonalInfoModal user={personalInfoUser} onClose={closePersonalInfo} onSaved={() => { setPersonalInfoOpen(false); notify('Personal information updated successfully.'); }} />}
    {passwordOpen && <div className="app-password-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closePassword(); }}>
      <div className="app-password-modal" role="dialog" aria-modal="true" aria-labelledby="profile-password-title" ref={dialogRef}>
        <div className="app-password-heading"><h2 id="profile-password-title">Change Password</h2><button type="button" aria-label="Close change password" onClick={closePassword} disabled={busy}><X size={19} /></button></div>
        <form onSubmit={submitPassword} noValidate>
          {Object.entries({ currentPassword: 'Current Password', newPassword: 'New Password', confirmPassword: 'Confirm New Password' }).map(([field, label]) => <div className="app-password-field" key={field}>
            <label htmlFor={`profile-${field}`}>{label}</label>
            <div className={`app-password-input${errors[field] ? ' invalid' : ''}`}>
              <input id={`profile-${field}`} type={visible[field] ? 'text' : 'password'} autoComplete={field === 'currentPassword' ? 'current-password' : 'new-password'} placeholder={field === 'currentPassword' ? 'Enter current password' : field === 'newPassword' ? 'Enter new password' : 'Enter confirm password'} value={passwords[field]} disabled={busy}
                aria-invalid={Boolean(errors[field])} aria-describedby={errors[field] ? `profile-${field}-error` : field === 'currentPassword' ? undefined : 'profile-password-help'}
                onBlur={() => {
                  touchedPasswords.current[field] = true;
                  const error = validatePasswordField(field, passwords);
                  setErrors(current => current[field] === error ? current : { ...current, [field]: error });
                }}
                onChange={event => {
                  const values = { ...passwords, [field]: event.target.value };
                  setPasswords(values);
                  setErrors(current => ({
                    ...current,
                    [field]: touchedPasswords.current[field] || current[field] ? validatePasswordField(field, values) : '',
                    ...(field === 'newPassword' && touchedPasswords.current.confirmPassword
                      ? { confirmPassword: validatePasswordField('confirmPassword', values) } : {}),
                    api: '',
                  }));
                }} />
              <button type="button" aria-label={`${visible[field] ? 'Hide' : 'Show'} ${label.toLowerCase()}`} aria-pressed={Boolean(visible[field])} onClick={() => setVisible(current => ({ ...current, [field]: !current[field] }))}>{visible[field] ? <EyeOff size={17} /> : <Eye size={17} />}</button>
            </div>
            {errors[field] && <p className="app-password-error" id={`profile-${field}-error`} role="alert">{errors[field]}</p>}
          </div>)}
          <p className="app-password-help" id="profile-password-help">{passwordHelp}</p>
          {errors.api && <p className="app-password-error" role="alert">{errors.api}</p>}
          <div className="app-password-actions"><button type="button" onClick={closePassword} disabled={busy}>Cancel</button><button type="submit" disabled={busy || ['currentPassword', 'newPassword', 'confirmPassword'].some(field => errors[field])}>{busy ? 'Updating...' : 'Update Password'}</button></div>
        </form>
      </div>
    </div>}
    <Toast toast={toast} onClose={() => setToast(null)} />
  </div>;
}
